/**
 * Copies Constructor.io identity onto Shopify cart attributes so they persist
 * through checkout and are available to backend.
 *
 * Sources → cart attributes:
 *   localStorage _constructorio_search_session_id → Constructorio_session_id
 *   localStorage _constructorio_search_client_id  → Constructorio_client_id
 *
 * Runs only when the cart is not empty. Listens to theme cart-update events
 * and Cookiebot consent (Constructor may write localStorage only after tracking consent).
 */
(function () {
  'use strict';

  const SESSION_LOCAL_STORAGE_KEY = '_constructorio_search_session_id';
  const CLIENT_LOCAL_STORAGE_KEY = '_constructorio_search_client_id';
  const ATTR_SESSION_ID = 'Constructorio_session_id';
  const ATTR_CLIENT_ID = 'Constructorio_client_id';

  let lastSyncedSignature = '';
  let syncInFlight = false;
  let identityRetryTimer = null;
  let identityRetries = 0;
  const MAX_IDENTITY_RETRIES = 20;
  const IDENTITY_RETRY_MS = 500;

  function getCartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function getCartJsUrl() {
    return `${getCartRoot()}cart.js`;
  }

  function getCartUpdateUrl() {
    return window.routes?.cart_update_url || `${getCartRoot()}cart/update.js`;
  }

  function getLocalStorageValue(key) {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw == null || raw === '') return '';
      try {
        const parsed = JSON.parse(raw);
        if (parsed == null || typeof parsed === 'object') return '';
        return String(parsed);
      } catch (e) {
        return raw;
      }
    } catch (e) {
      return '';
    }
  }

  function getConstructorioAttributes() {
    const attributes = {};
    const sessionId = getLocalStorageValue(SESSION_LOCAL_STORAGE_KEY);
    const clientId = getLocalStorageValue(CLIENT_LOCAL_STORAGE_KEY);

    if (sessionId) attributes[ATTR_SESSION_ID] = sessionId;
    if (clientId) attributes[ATTR_CLIENT_ID] = clientId;

    return attributes;
  }

  function signature(attributes) {
    return Object.keys(attributes)
      .sort()
      .map((key) => `${key}=${attributes[key]}`)
      .join('&');
  }

  function hasCartState(cartData) {
    return Boolean(
      cartData &&
        (typeof cartData.item_count === 'number' ||
          Array.isArray(cartData.items) ||
          cartData.variant_id ||
          cartData.product_id ||
          cartData.key),
    );
  }

  function isCartEmpty(cartData) {
    if (!cartData) return true;
    if (typeof cartData.item_count === 'number') return cartData.item_count < 1;
    if (Array.isArray(cartData.items)) return cartData.items.length === 0;
    // cart/add.js returns the added line item — cart is not empty.
    if (cartData.variant_id || cartData.product_id || cartData.key) return false;
    return true;
  }

  function attributesAlreadySet(cartData, attributes) {
    const current = cartData?.attributes;
    if (!current) return false;
    return Object.keys(attributes).every((key) => String(current[key] || '') === attributes[key]);
  }

  function scheduleIdentityRetry() {
    if (identityRetryTimer != null || identityRetries >= MAX_IDENTITY_RETRIES) return;
    identityRetries += 1;
    identityRetryTimer = setTimeout(() => {
      identityRetryTimer = null;
      syncConstructorCartAttributes(null);
    }, IDENTITY_RETRY_MS);
  }

  async function fetchCart() {
    const response = await fetch(getCartJsUrl());
    if (!response.ok) return null;
    return response.json();
  }

  async function syncConstructorCartAttributes(cartData) {
    if (syncInFlight) return;

    const attributes = getConstructorioAttributes();
    const incomplete = !attributes[ATTR_SESSION_ID] || !attributes[ATTR_CLIENT_ID];
    if (!Object.keys(attributes).length) {
      scheduleIdentityRetry();
      return;
    }

    const nextSignature = signature(attributes);
    if (nextSignature === lastSyncedSignature) {
      if (incomplete) scheduleIdentityRetry();
      return;
    }

    syncInFlight = true;
    let cartWasEmpty = false;

    try {
      let cart = cartData;
      if (!hasCartState(cart)) {
        cart = await fetchCart();
      }

      if (isCartEmpty(cart)) {
        cartWasEmpty = true;
        return;
      }

      if (attributesAlreadySet(cart, attributes)) {
        lastSyncedSignature = nextSignature;
        return;
      }

      const response = await fetch(getCartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      if (response.ok) lastSyncedSignature = nextSignature;
    } catch (e) {
      console.error('Constructor cart attributes sync failed:', e);
    } finally {
      syncInFlight = false;
      if (incomplete && !cartWasEmpty) scheduleIdentityRetry();
    }
  }

  function syncExistingCart() {
    return syncConstructorCartAttributes(null);
  }

  function waitAndSubscribe(attempt) {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        syncConstructorCartAttributes(event?.cartData);
      });
      syncExistingCart();
      return;
    }

    if (attempt < 120) {
      requestAnimationFrame(() => waitAndSubscribe(attempt + 1));
    }
  }

  waitAndSubscribe(0);

  window.addEventListener('CookiebotOnAccept', syncExistingCart);
  window.addEventListener('CookiebotOnConsentReady', syncExistingCart);
})();
